import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
  DecisionList,
  InlineRelatedLinks,
} from "@/components/DecisionPageLayout";
import { CheckCircle2 } from "lucide-react";

const seo = {
  title: "FEHB TMS Coverage | Neuro Wellness TMS Centers of America",
  description:
    "Wondering if your federal health plan covers TMS? We explain what FEHB may cover, what it looks for, and how we verify your benefits for free. Call 954-827-9793.",
  canonical: "/fehb-tms-coverage",
  keywords:
    "FEHB TMS coverage, federal employee health benefits TMS, FEP Blue TMS, FEHB depression treatment coverage",
};

const relatedLinks = [
  { title: "Federal Employee Hub", href: "/federal-employee-mental-health" },
  { title: "Federal Employee TMS", href: "/federal-employee-tms" },
  {
    title: "Does Federal BCBS Cover SAINT?",
    href: "/does-federal-bcbs-cover-saint",
  },
  {
    title: "Federal Employee Insurance Verification",
    href: "/federal-employee-insurance-verification",
  },
  { title: "How Insurance Works", href: "/how-insurance-works" },
];

export default function FebhTmsCoverage() {
  return (
    <DecisionPageLayout
      seo={seo}
      badge="FEHB Coverage"
      title="Will Your Federal Health Plan Cover TMS?"
      subtitle="If you have FEHB — the federal employee health program — TMS may be covered when certain criteria are met. Here's what your plan looks for, and how we help you find out."
    >
      <DecisionSection heading="The short answer">
        <p>
          Yes, TMS may be covered under your federal health plan. But it depends
          on your specific carrier, your plan type, your diagnosis, and your
          treatment history. We verify all of this for you — at no cost.
        </p>
        <p>
          The good news: the federal program's medical policy specifically names
          SAINT (the accelerated five-day TMS protocol) as an example of a
          treatment that may be covered. Many commercial insurance plans still
          classify accelerated TMS as experimental. The federal plan does not.
        </p>
      </DecisionSection>

      <DecisionSection heading="What your plan looks for">
        <p>
          Your federal health plan uses three main criteria to decide whether
          TMS is medically necessary. You don't need to figure these out alone —
          our team helps you gather everything.
        </p>
        <div className="mt-4 space-y-4">
          <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-accent" />
              <div>
                <h3 className="font-headings text-base font-bold text-primary">
                  1. A confirmed diagnosis of severe depression
                </h3>
                <p className="mt-1 text-[16px] leading-relaxed text-muted-foreground">
                  Your doctor needs to confirm you have severe major depressive
                  disorder, documented using standard rating tools. This is a
                  clinical determination — not something you assess yourself.
                </p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-accent" />
              <div>
                <h3 className="font-headings text-base font-bold text-primary">
                  2. Antidepressants haven't worked well enough
                </h3>
                <p className="mt-1 text-[16px] leading-relaxed text-muted-foreground">
                  You need to meet at least one of these:
                </p>
                <ul className="mt-2 space-y-1.5">
                  <li className="flex items-start gap-2">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    <span className="text-[16px] leading-relaxed text-muted-foreground">
                      You've tried at least two antidepressants from different
                      classes, for at least six weeks each at a proper dose,
                      without enough improvement
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    <span className="text-[16px] leading-relaxed text-muted-foreground">
                      You couldn't tolerate a therapeutic dose because of side
                      effects
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    <span className="text-[16px] leading-relaxed text-muted-foreground">
                      You've responded to TMS before, in a previous episode
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    <span className="text-[16px] leading-relaxed text-muted-foreground">
                      You're a candidate for ECT, but ECT wouldn't be a better
                      option
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-accent" />
              <div>
                <h3 className="font-headings text-base font-bold text-primary">
                  3. You've also tried therapy
                </h3>
                <p className="mt-1 text-[16px] leading-relaxed text-muted-foreground">
                  Your plan also requires that you've tried talk therapy known
                  to be effective for depression, for an adequate length of
                  time, documented by your provider. This carries the same
                  weight as the medication requirement.
                </p>
              </div>
            </div>
          </div>
        </div>
      </DecisionSection>

      <DecisionSection heading="Important details">
        <DecisionSubHeading>What's covered — and what isn't</DecisionSubHeading>
        <p>
          TMS is covered for major depressive disorder. It is not covered for
          other conditions like bipolar disorder, OCD, schizophrenia, or
          migraines under the federal plan's policy. Patients must be over age
          15.
        </p>
        <DecisionSubHeading>Your plan options</DecisionSubHeading>
        <p>
          Federal health plans come in different options — Standard, Basic, and
          Focus. Benefits differ across these. Under the Focus option, benefits
          are generally not available for out-of-network providers except in
          emergencies. Postal employees and annuitants are now covered under a
          separate program (PSHB) with its own plans.
        </p>
        <DecisionSubHeading>Prior authorization</DecisionSubHeading>
        <p>
          TMS is not on the national prior-approval list for Standard and Basic
          options — but requirements vary by the local Blue plan on the back of
          your card, and the Focus option has its own list. Never assume
          authorization isn't required. We verify this for you.
        </p>
      </DecisionSection>

      <DecisionSection heading="What we do for you — at no cost">
        <p>
          When you reach out, our team contacts your federal health plan,
          confirms what's covered, checks whether prior authorization is
          required, and gives you a written summary of your costs — all before
          you commit to anything. We submit the authorization request and handle
          appeals if it's denied.
        </p>
        <p>
          Call{" "}
          <a
            href="tel:9548279793"
            className="text-accent font-semibold hover:underline"
          >
            (954) 827-9793
          </a>{" "}
          to get started.
        </p>
        <p className="text-sm text-muted-foreground pt-2">
          Coverage information current as of 2026. Federal medical policy is
          revised annually. This page is education, not a coverage
          determination.
        </p>
      </DecisionSection>

      <InlineRelatedLinks heading="Related Pages" links={relatedLinks} />
    </DecisionPageLayout>
  );
}
